import { t } from '../i18n/index.ts';
import type { Cue } from '@core/model/cue.ts';

/**
 * Extension `infoCue` — full-timeline placeholder painted on #mainSub / #secondSub
 * while download / enhance / whisper jobs run.
 */
export function infoCue(text: string): Cue[] {
  return [
    {
      start: 0,
      end: 86400000,
      info: true,
      text,
      segs: [{ start: 0, end: 86400000, text }],
    },
  ];
}

/** Extension `web_service_loading` — AI translate cue.loading overlay. */
export function serviceLoadingText(): string {
  return t('serviceLoading');
}

/** Extension `web_service_downloading`. */
export function serviceDownloadingText(): string {
  return t('serviceDownloading');
}

/** Extension `web_service_enhanced` — Enhanced + creator-mapped track suffix. */
export function serviceEnhancedSuffix(): string {
  return t('serviceEnhanced');
}

/**
 * Extension `web_service_generating` with optional percent.
 * Template uses ` (#)` placeholder (replaced with ` (N%)` or removed).
 */
export function serviceGeneratingText(progress?: number): string {
  return t('serviceGenerating').replace(
    ' (#)',
    progress != null && progress > 0 ? ` (${Math.round(progress)}%)` : '',
  );
}

/**
 * Extension `web_service_queuing`.
 * Caption jobs pass raw `position`; AI Batch translation uses `position - 1`.
 */
export function serviceQueuingText(position: number, progress?: number): string {
  return t('serviceQueuing')
    .replace('#', String(position))
    .replace(' (@)', progress != null && progress > 0 ? ` (${Math.round(progress)}%)` : '');
}

/** Progress line while polling enhance / whisper (extension memberBackend). */
export function memberJobProgressText(json: {
  phase?: string;
  position?: number;
  progress?: number;
}): string {
  if (json.phase === 'download') return serviceDownloadingText();
  if (json.position != null && json.position > 1) {
    return serviceQueuingText(json.position, json.progress);
  }
  return serviceGeneratingText(json.progress);
}

/** Progress line while polling AI Batch translate (extension translationLegacyBridge). */
export function batchTranslateProgressText(json: {
  position?: number;
  progress?: number;
}): string {
  if (json.position != null && json.position > 1) {
    return serviceQueuingText(json.position - 1, json.progress);
  }
  return serviceGeneratingText(json.progress);
}

/** Extension `web_service_dismiss_*` / members_only for caption jobs. */
export function serviceDismissText(desc?: string): string {
  switch (desc) {
    case 'members_only':
      return t('serviceMemberOnly');
    case 'info':
      return t('serviceDismissInfo');
    case 'length':
      return t('serviceDismissLength');
    case 'live':
      return t('serviceDismissLive');
    case 'source':
      return t('serviceDismissSource');
    case 'enhance':
      return t('serviceDismissEnhance');
    case 'enhance_timeout':
      return t('serviceDismissEnhanceTimeout');
    case 'upstream':
      return t('serviceDismissUpstream');
    case 'cancelled':
      return t('serviceDismissCancelled');
    default:
      return t('serviceDismissUnknown');
  }
}

export function translationMemberOnlyText(): string {
  return t('translationMemberOnly');
}

export function translationFailedText(): string {
  return t('translationFailed');
}

export function translationQuotaText(): string {
  return t('translationQuota');
}
